// src/hooks/useInsuranceActivity.ts

import { useCallback } from "react";

import { useNotifications } from "../contexts/NotificationContext";

import type {
  ActivityLog,
} from "../contexts/InsuranceContextType";

interface UseInsuranceActivityOptions {
  setActivityLogs: React.Dispatch<
    React.SetStateAction<ActivityLog[]>
  >;
}

interface UseInsuranceActivityReturn {
  logActivity: (
    activity: Omit<ActivityLog, "id" | "timestamp">
  ) => void;

  getRecentActivity: (
    activityLogs: ActivityLog[],
    limit?: number
  ) => ActivityLog[];
}

const MAX_ACTIVITY_LOGS = 100;

const createActivityId = (): string =>
  `ACT-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 11)}`;

export function useInsuranceActivity({
  setActivityLogs,
}: UseInsuranceActivityOptions): UseInsuranceActivityReturn {
  const { pushNotification } = useNotifications();

  const logActivity = useCallback(
    (
      activity: Omit<
        ActivityLog,
        "id" | "timestamp"
      >
    ) => {
      const newActivity: ActivityLog = {
        ...activity,
        id: createActivityId(),
        timestamp: new Date().toISOString(),
      };

      setActivityLogs((current) =>
        [newActivity, ...current].slice(
          0,
          MAX_ACTIVITY_LOGS
        )
      );

      pushNotification({
        title: activity.action,
        message: activity.description,
        type: "update",
        source: "insurance",
        priority: "normal",
      });
    },
    [pushNotification, setActivityLogs]
  );

  const getRecentActivity = useCallback(
    (
      activityLogs: ActivityLog[],
      limit = 10
    ) => {
      return activityLogs.slice(
        0,
        Math.max(0, limit)
      );
    },
    []
  );

  return {
    logActivity,
    getRecentActivity,
  };
}